import { useQuery } from '@apollo/client/react';
import { useSearchParams } from 'react-router';
import { ASSET_GROUPS_QUERY } from '@scio3/graphql-client';
import { useTeamStore } from '@/stores/team-store';
import { usePermissions } from '@/hooks/usePermissions';
import { folderOwnerTeamId, withOwnerTeamId } from '@/lib/folder-security';

interface GroupNode {
  _id: string;
  name: string;
  type?: string;
  path?: string;
  teamId?: string;
  isFolderSecurityEdit?: boolean;
  isFolderSecurityView?: boolean;
  securities?: Array<{ userId: string; role: string }>;
  securityTeams?: string[];
}

interface GroupsQueryResult {
  groups: {
    page: {
      edges: Array<{ node: GroupNode }>;
    };
  };
}

/**
 * Permission gate for the Designer page.
 *
 * Uses the URL's `path` parameter (the folder containing the asset) to check
 * if the current user may WRITE in that folder. The backend groups resolver
 * filters out folders the user cannot see, so an empty result = deny; a folder
 * that is found still has to pass the folder-aware check (scio3#2550) — a
 * View-only folder, or another team's folder the user is not admin of without
 * an Edit share, is visible but not writable.
 *
 * Gates both edit flows (asset_id + path) and creation flows (path only).
 */
export function useDesignerPermissionGate() {
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const [searchParams] = useSearchParams();

  // Use the URL's path parameter — this is the folder the asset lives in.
  // More reliable than fetching the asset first because the asset's DB path
  // may be null/undefined for some asset types.
  const urlPath = searchParams.get('path');
  // Cross-team shared folder: the owning team rides on ?teamId= (see designer-bridge-handlers).
  const urlTeamId = searchParams.get('teamId') || undefined;
  const isCrossTeam = !!urlTeamId && urlTeamId !== activeTeamId;
  // Engage flows (Kiosk Designer Pro) pass engage=1 — folders use GROUP_TYPES.ENGAGE
  const isEngage = searchParams.get('engage') === '1';
  const groupType = isEngage ? 'ENGAGE' : 'ASSET';

  // Parse path to get parent path and folder name (same pattern as useCurrentGroup)
  const isSubfolder = !!urlPath && urlPath !== '/' && urlPath.length > 1;
  const lastSlash = urlPath ? urlPath.lastIndexOf('/') : -1;
  const parentPath = isSubfolder && lastSlash > 0 ? urlPath.substring(0, lastSlash) : null;
  const folderName = isSubfolder ? (urlPath?.substring(lastSlash + 1) ?? null) : null;

  // Fetch the folder with security fields to check if user has access.
  // Runs for both edit (asset_id present) and creation (path-only) flows.
  const shouldFetchFolder = isSubfolder && !!folderName && !!activeTeamId;
  const { data: groupData, loading: groupLoading } = useQuery<GroupsQueryResult>(
    ASSET_GROUPS_QUERY,
    {
      variables: {
        teamId: activeTeamId,
        query: {
          type: groupType,
          // The folder's owning team, so a cross-team shared folder is looked up in its own
          // tree (same as useCurrentGroup).
          ...(urlTeamId ? { teamId: urlTeamId } : {}),
          path: parentPath,
          name: folderName,
        },
        // Cross-team: the backend $or may also return a same-name folder from the user's own
        // team — fetch a few and pick the one owned by urlTeamId.
        first: isCrossTeam ? 10 : 1,
      },
      skip: !shouldFetchFolder,
      fetchPolicy: 'network-only',
    },
  );

  const nodes = groupData?.groups?.page?.edges?.map((e) => e.node) ?? [];
  // A loaded folder with no teamId is a legacy Default-team folder (backend: `teamId || ROOT`).
  const match = isCrossTeam ? nodes.find((n) => folderOwnerTeamId(n) === urlTeamId) : nodes[0];
  const parentFolder = match ? withOwnerTeamId(match) : null;

  // Folder-aware access (Level 2) for the folder the Designer writes into.
  const { hasFolderAccess, folderLevelSecurity } = usePermissions(
    parentFolder ? { parentFolder } : {},
  );

  return evaluateDesignerFolderGate({
    isSubfolder,
    isLoading: groupLoading,
    folderFound: !!parentFolder,
    isFolderInActiveTeam: !!parentFolder && parentFolder.teamId === activeTeamId,
    hasFolderAccess,
    folderLevelSecurity,
  });
}

/**
 * Pure decision for the Designer folder gate (exported for tests).
 * Root / no folder → allowed; loading → pending; folder not visible → deny (fail closed);
 * visible but not writable → deny ONLY for a folder owned by ANOTHER team (scio3#2550: a
 * View-only share, or one not shared to the active team, when the user is not admin of the
 * folder's team), and only with folder security ON. A folder in the user's ACTIVE team keeps the
 * pre-#2550 "found = allowed" rule for every role, as does a folder-security-OFF account.
 */
export function evaluateDesignerFolderGate({
  isSubfolder,
  isLoading,
  folderFound,
  isFolderInActiveTeam,
  hasFolderAccess,
  folderLevelSecurity,
}: {
  isSubfolder: boolean;
  isLoading: boolean;
  folderFound: boolean;
  isFolderInActiveTeam: boolean;
  hasFolderAccess: boolean;
  folderLevelSecurity: boolean;
}): { isAllowed: boolean; isLoading: boolean } {
  if (!isSubfolder) return { isAllowed: true, isLoading: false };
  if (isLoading) return { isAllowed: false, isLoading: true };
  if (!folderFound) return { isAllowed: false, isLoading: false };
  return {
    isAllowed: !folderLevelSecurity || isFolderInActiveTeam || hasFolderAccess,
    isLoading: false,
  };
}
